import { useState } from 'react'
import { Search, Filter, CheckCircle, XCircle, Settings, Send, Clock, Loader2, RefreshCw } from 'lucide-react'
import { useAuditLogs } from '@/hooks/useAuditLogs'
import { shortenAddress } from '@/lib/utils'

const actionConfig: Record<string, { icon: any; color: string; bgColor: string; label: string }> = {
  REQUEST_APPROVED: { icon: CheckCircle, color: 'text-accent-400', bgColor: 'bg-accent-500/20', label: 'Aprobado' },
  REQUEST_REJECTED: { icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-500/20', label: 'Rechazado' },
  RELEASE_CONFIRMED: { icon: Send, color: 'text-blue-400', bgColor: 'bg-blue-500/20', label: 'Enviado' },
  CONFIG_UPDATED: { icon: Settings, color: 'text-purple-400', bgColor: 'bg-purple-500/20', label: 'Config' },
  WALLETS_UPDATED: { icon: Settings, color: 'text-purple-400', bgColor: 'bg-purple-500/20', label: 'Wallets' },
  BRIDGE_PAUSED: { icon: Clock, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20', label: 'Pausado' },
  BRIDGE_RESUMED: { icon: CheckCircle, color: 'text-accent-400', bgColor: 'bg-accent-500/20', label: 'Activado' },
  REQUEST_CREATED: { icon: Clock, color: 'text-blue-400', bgColor: 'bg-blue-500/20', label: 'Creado' },
  REQUEST_CANCELLED: { icon: XCircle, color: 'text-orange-400', bgColor: 'bg-orange-500/20', label: 'Cancelado' },
}

export function AuditPage() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('all')

  const { logs, total, isLoading, refetch } = useAuditLogs({
    action: filter !== 'all' ? filter : undefined,
    limit: 100,
  })

  // Filter by search locally
  const filteredLogs = logs.filter((log) => {
    if (search) {
      const searchLower = search.toLowerCase()
      const description = getDescription(log)
      return (
        description.toLowerCase().includes(searchLower) ||
        (log.adminId && log.adminId.toLowerCase().includes(searchLower)) ||
        (log.requestId && log.requestId.toLowerCase().includes(searchLower))
      )
    }
    return true
  })

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  function getDescription(log: any): string {
    const details = log.details || {}
    switch (log.action) {
      case 'REQUEST_APPROVED':
        return `Solicitud ${log.requestId ? `#${log.requestId.slice(0, 8)}` : ''} aprobada`
      case 'REQUEST_REJECTED':
        return `Solicitud ${log.requestId ? `#${log.requestId.slice(0, 8)}` : ''} rechazada${details.reason ? `: ${details.reason}` : ''}`
      case 'RELEASE_CONFIRMED':
        return `Envío confirmado ${log.requestId ? `para solicitud #${log.requestId.slice(0, 8)}` : ''}`
      case 'CONFIG_UPDATED':
        return formatConfigUpdate(details)
      case 'WALLETS_UPDATED':
        return 'Direcciones de wallets actualizadas'
      case 'BRIDGE_PAUSED':
        return 'Bridge pausado'
      case 'BRIDGE_RESUMED':
        return 'Bridge activado'
      case 'REQUEST_CREATED':
        return `Nueva solicitud ${log.requestId ? `#${log.requestId.slice(0, 8)}` : ''} creada`
      case 'REQUEST_CANCELLED':
        return `Solicitud ${log.requestId ? `#${log.requestId.slice(0, 8)}` : ''} cancelada`
      default:
        return log.action
    }
  }

  function formatConfigUpdate(details: any): string {
    const changes: string[] = []
    if (details.feePercentage !== undefined) changes.push(`comisión: ${details.feePercentage}%`)
    if (details.isPaused !== undefined) changes.push(details.isPaused ? 'pausado' : 'activado')
    if (details.minBridgeAmount !== undefined) changes.push('límite mínimo actualizado')
    if (details.maxBridgeAmount !== undefined) changes.push('límite máximo actualizado')
    if (details.processingMode !== undefined) changes.push(`modo: ${details.processingMode}`)
    return changes.length > 0 ? `Configuración actualizada: ${changes.join(', ')}` : 'Configuración actualizada'
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Registro de Auditoría</h1>
        <div className="flex items-center gap-3">
          <span className="text-sm text-dark-400">{total} registros</span>
          <button
            onClick={() => refetch()}
            className="p-2 rounded-lg hover:bg-dark-800 transition-colors text-dark-400 hover:text-white"
            title="Actualizar"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" />
          <input
            type="text"
            placeholder="Buscar por descripción, admin o ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-dark-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="input w-auto text-sm"
          >
            <option value="all">Todas las acciones</option>
            <option value="REQUEST_APPROVED">Aprobaciones</option>
            <option value="REQUEST_REJECTED">Rechazos</option>
            <option value="RELEASE_CONFIRMED">Envíos</option>
            <option value="CONFIG_UPDATED">Configuración</option>
            <option value="BRIDGE_PAUSED">Pausas</option>
            <option value="REQUEST_CREATED">Solicitudes creadas</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && logs.length === 0 && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-uni-500" />
        </div>
      )}

      {/* Logs List */}
      {!isLoading || logs.length > 0 ? (
        <div className="space-y-2">
          {filteredLogs.map((log) => {
            const config = actionConfig[log.action] || actionConfig.CONFIG_UPDATED
            const Icon = config.icon

            return (
              <div key={log.id} className="card p-4 hover:border-dark-700 transition-all">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`w-10 h-10 ${config.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                        {config.label}
                      </span>
                      {log.requestId && (
                        <span className="text-xs text-dark-500">#{log.requestId.slice(0, 8)}</span>
                      )}
                    </div>
                    <p className="text-white text-sm">{getDescription(log)}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-dark-500">
                      {log.adminId && (
                        <span>Admin: <span className="font-mono">{shortenAddress(log.adminId)}</span></span>
                      )}
                      <span>{formatDate(log.createdAt)}</span>
                      {log.txHash && (
                        <span className="font-mono">{shortenAddress(log.txHash)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}

          {filteredLogs.length === 0 && !isLoading && (
            <div className="card p-8 text-center text-dark-400">
              No se encontraron registros
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}
